import { createAsyncThunk, createSlice, UnknownAction } from '@reduxjs/toolkit';
import PaymentSettingService from '../../../api/services/PaymentSettingsService';
import { RootState } from '../../../store';
import { PaymentMethodsResponse } from '../Components/select-payment-method/types';

type PaymentSettingState = {
  paymentMethods: PaymentMethodsResponse[] | null;
  status: 'pending' | 'success' | 'failure';
  isLoading: boolean;
  isSubmitting: boolean;
};

const initialState: PaymentSettingState = {
  paymentMethods: null,
  status: 'pending',
  isLoading: false,
  isSubmitting: false,
};

const ACTIONS_PREFIX = 'paymentSettings';

export const getPaymentMethodsThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getPaymentMethodsThunk`,
  PaymentSettingService.getPaymentMethods,
);

export const getClientSecretThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getClientSecretThunk`,
  PaymentSettingService.getClientSecret,
);

export const cancelPaymentThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/cancelPaymentThunk`,
  PaymentSettingService.cancelPayment,
);

const paymentSettingsSlice = createSlice({
  name: 'paymentSettings',
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsSubmitting: (state, action) => {
      state.isSubmitting = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getPaymentMethodsThunk.fulfilled, (state, action) => {
      const paymentMethods = action.payload.resource as PaymentMethodsResponse[];
      state.paymentMethods = paymentMethods;
    });
    builder.addMatcher(
      (action: UnknownAction) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/fulfilled'),
      (state) => {
        Object.assign(state, {
          isLoading: false,
          status: 'success',
        });
      },
    );
    builder.addMatcher(
      (action: UnknownAction) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/pending'),
      (state) => {
        Object.assign(state, {
          isLoading: true,
          status: 'loading',
          error: '',
        });
      },
    );
    builder.addMatcher(
      (action: UnknownAction) =>
        (action.type as string).startsWith(ACTIONS_PREFIX) &&
        (action.type as string).endsWith('/rejected'),
      (state, action) => {
        Object.assign(state, {
          isLoading: false,
          status: 'failure',
          error: action || '',
        });
      },
    );
  },
});

// ACTIONS
export const { setIsLoading, setIsSubmitting } = paymentSettingsSlice.actions;

// selectors
export const paymentSettingsState = (state: RootState) => state.paymentSettings;

export default paymentSettingsSlice.reducer;
